// 加工指示エントリのスナップショット生成・キャッシュ・切り替え適用をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createKakousijiEntrySnapshotService(deps) {
    const {
        DeepCopy,
        DicVal,
        G_id,
        GridData,
        KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE,
        KAKOUSIJI_SAMPLE_GROUP_KEY,
        KAKOUSIJI_S_LO_TYPE_OFFSET,
        SetGridData,
        activeKakousijiKeyRef,
        activeKakousijiSampleGroupEntryKey,
        activeKakousijiSummaryItem,
        applyClientOnlyEntryDefaults,
        assignEntryDataCurrent,
        buildKakousijiSummaryRowFromEntry,
        clearKakousijiSnapshotSystemValues,
        columninfo,
        columns,
        dispatch,
        entrydata,
        entrydataRef,
        entryrerender,
        gridlist,
        groupKakousijiSampleSummaryRows,
        isEmpty,
        isKakousijiPanelType,
        isKakousijiSampleGroupSummaryItem,
        kakousijiEntryCacheRef,
        kakousijiGroupControlStateRef,
        KAKOUSIJI_QUALITY_BASE_PANEL_TYPE,
        kakousijiQualityGroupControlStateRef,
        setKakousijiQualityGroupControlState,
        setKakousijiOrderHistoryData,
        applyFukushizaiScopeDisplayRows,
        normalizeKakousijiDemeritDisplayRows,
        pickKakousijiQualityScopeRepresentativeRows,
        kakousijiWashSourceRowsRef,
        buildHinbanWashSaveRows,
        HINBAN_WASH_PANEL_TYPE,
        refreshKakousijiNouhinGridColumns,
        kakousijiImportedSummaryRowsRef,
        kakousijiSampleGroupEntryKeys,
        kakousijiSummaryList,
        mergeKakousijiRepresentativeRowToEntry,
        normalizeKakousijiDemandRows,
        normalizeKakousijiKubunValue,
        normalizeKakousijiSampleDetailForDisplayByType,
        normalizeKakousijiSampleDetailForStoreByType,
        resolveCurrentOppiCode,
        resolveKakousijiGridPanelByType,
        resolveKakousijiGroupItemContext,
        setActiveKakousijiKey,
        setEntryGridRows,
        setKakousijiGroupControlState,
        setKakousijiSummaryList,
        snapshotKakousijiSampleGroupDisplayEntry,
        storeRowData,
        syncKakousijiAnchorGridRows,
        syncKakousijiSamplePanelsFromPdtc,
        textfields,
        textfieldsall
    } = deps;

    function mapKakousijiPanelTypeToHinban(type) {
        const numericType = Number(String(isEmpty(type) ? "" : type).trim());
        if (!Number.isFinite(numericType)) return "";
        return String(numericType + KAKOUSIJI_S_LO_TYPE_OFFSET);
    }
    function mapHinbanPanelTypeToKakousiji(type) {
        const numericType = Number(String(isEmpty(type) ? "" : type).trim());
        if (!Number.isFinite(numericType)) return "";
        return String(numericType - KAKOUSIJI_S_LO_TYPE_OFFSET);
    }
    function collectKakousijiPanelColumnIds() {
        const ids = [];
        const protectedEntryColumnIds = ["PDT_HBSEASONCD", "PDT_SYOHINCD", "PDT_BRDCODE", "PDT_HINMEINM", "PDT_SBRCODE"];
        const protectedDisplaySuffixes = ["_nm", "_NM", "_cd", "_CD"];
        const isProtectedEntryColumnId = (id) => {
            const normalized = String(id ?? "").trim();
            if (protectedEntryColumnIds.includes(normalized)) return true;
            return protectedDisplaySuffixes.some((suffix) => {
                if (!normalized.endsWith(suffix)) return false;
                return protectedEntryColumnIds.includes(normalized.slice(0, -suffix.length));
            });
        };
        const add = (value) => {
            const id = String(value ?? "").trim();
            if (id === "" || isProtectedEntryColumnId(id) || ids.includes(id)) return;
            ids.push(id);
        };
        const addWithDisplayKeys = (value) => {
            const id = String(value ?? "").trim();
            if (id === "") return;
            add(id);
            add(id + "_nm");
            add(id + "_NM");
            add(id + "_cd");
            add(id + "_CD");
        };
        const collect = (source) => {
            (Array.isArray(source) ? source : []).forEach((col) => {
                if (!isKakousijiPanelType(DicVal(col, "type", ""))) return;
                addWithDisplayKeys(DicVal(col, "columnid", DicVal(col, "field", "")));
                (Array.isArray(DicVal(col, "trigger", null)) ? DicVal(col, "trigger", []) : []).forEach((trigger) => {
                    addWithDisplayKeys(DicVal(trigger, "columnid", ""));
                });
            });
        };
        collect(columninfo);
        collect(columns);
        collect(textfieldsall);
        collect(textfields);
        return ids;
    }
    function collectBasePanelColumnIds() {
        const ids = [];
        const add = (value) => {
            const id = String(value ?? "").trim();
            if (id === "" || ids.includes(id)) return;
            ids.push(id);
        };
        (Array.isArray(textfieldsall) ? textfieldsall : []).forEach((col) => {
            if (String(DicVal(col, "logpid", "")) !== G_id.BASE) return;
            add(DicVal(col, "columnid", DicVal(col, "field", "")));
        });
        (Array.isArray(columninfo) ? columninfo : []).forEach((col) => {
            if (String(DicVal(col, "logpid", "")) !== G_id.BASE) return;
            add(DicVal(col, "columnid", DicVal(col, "field", "")));
        });
        (Array.isArray(columns) ? columns : []).forEach((col) => {
            if (String(DicVal(col, "logpid", "")) !== G_id.BASE) return;
            add(DicVal(col, "columnid", DicVal(col, "field", "")));
        });
        return ids;
    }
    function clearActiveKakousijiSelectionData() {
        if (typeof setKakousijiOrderHistoryData === "function") {
            setKakousijiOrderHistoryData({ seihin: [], sozai: [], fuzoku: [], fukushizai: [] });
        }
        if (isEmpty(entrydataRef.current)) return;
        const nextEntry = { ...entrydataRef.current };
        collectKakousijiPanelColumnIds().forEach((columnId) => {
            nextEntry[columnId] = "";
        });
        assignEntryDataCurrent(nextEntry, "nextEntry");
        (Array.isArray(gridlist) ? gridlist : []).forEach((item) => {
            if (!isKakousijiPanelType(DicVal(item, "type", ""))) return;
            SetGridData({ id: item.id }, []);
        });
        if (!isEmpty(kakousijiWashSourceRowsRef)) kakousijiWashSourceRowsRef.current = [];
        dispatch(entrydata({ ...entrydataRef.current }));
        dispatch(entryrerender());
    }
    function buildKakousijiEntrySnapshotData(sourceData, oppiCode, kubunValue) {
        const next = {};
        collectKakousijiPanelColumnIds().forEach((columnId) => {
            next[columnId] = DicVal(sourceData, columnId, "");
        });
        Object.keys(next).forEach((key) => {
            if (/_NO$/i.test(key)) next[key] = "";
            if (/_OPPICD$/i.test(key)) next[key] = oppiCode;
            if (/_INVALID$/i.test(key)) next[key] = "0";
        });
        if ("SSD_OPPICD" in next) next.SSD_OPPICD = oppiCode;
        if ("SSDX_OPPICD" in next) next.SSDX_OPPICD = oppiCode;
        if ("SSDX_KBN" in next) next.SSDX_KBN = kubunValue;
        return next;
    }
    function buildKakousijiEntrySnapshotDetail(sourceDetail, oppiCode, kubunValue) {
        const next = {};
        Object.keys(isEmpty(sourceDetail) ? {} : sourceDetail).forEach((type) => {
            const sourceRows = normalizeKakousijiSampleDetailForStoreByType(type, DicVal(sourceDetail, type, []));
            next[type] = DeepCopy(sourceRows).map((row) => clearKakousijiSnapshotSystemValues(row, oppiCode, kubunValue, type));
        });
        return next;
    }
    function emptyKakousijiSnapshot(oppiCode, kubunValue) {
        const data = buildKakousijiEntrySnapshotData({}, oppiCode, kubunValue);
        const detail = {};
        (Array.isArray(gridlist) ? gridlist : []).forEach((item) => {
            if (!isKakousijiPanelType(DicVal(item, "type", ""))) return;
            detail[String(DicVal(item, "type", "")).trim()] = [];
        });
        return { data: data, detail: detail };
    }
    function setKakousijiEntryCache(key, snapshot) {
        const cacheKey = String(key ?? "").trim();
        if (cacheKey === "") return;
        kakousijiEntryCacheRef.current[cacheKey] = DeepCopy(snapshot);
        const snapshotData = DicVal(snapshot, "data", {});
        const dataSsdNo = String(DicVal(snapshotData, "SSD_NO", DicVal(snapshotData, "SSD_SIJINO", ""))).trim();
        if (dataSsdNo !== "" && dataSsdNo !== cacheKey) {
            kakousijiEntryCacheRef.current[dataSsdNo] = DeepCopy(snapshot);
        }
    }
    function getKakousijiEntryCache(key) {
        const cacheKey = String(key ?? "").trim();
        if (cacheKey === "") return null;
        return DicVal(kakousijiEntryCacheRef.current, cacheKey, null);
    }
    function getKakousijiEntryCacheForSummaryItem(summaryItem) {
        if (isEmpty(summaryItem)) return null;
        if (isKakousijiSampleGroupSummaryItem(summaryItem)) {
            const activeSampleKey = activeKakousijiSampleGroupEntryKey(summaryItem);
            const activeSampleCache = getKakousijiEntryCache(activeSampleKey);
            if (!isEmpty(activeSampleCache)) return activeSampleCache;
        }
        const key = String(DicVal(summaryItem, "key", "")).trim();
        const code = String(DicVal(summaryItem, "code", DicVal(summaryItem, "SSD_NO", ""))).trim();
        const byKey = getKakousijiEntryCache(key);
        if (!isEmpty(byKey)) return byKey;
        if (code !== "" && code !== key) return getKakousijiEntryCache(code);
        return null;
    }
    function resolveKakousijiDetailTargetPanelType(rawType) {
        const sourceType = String(rawType ?? "").trim();
        if (sourceType === "") return "";
        const mappedType = mapKakousijiPanelTypeToHinban(sourceType);
        const sourcePanelExists = !isEmpty(resolveKakousijiGridPanelByType(sourceType));
        const mappedPanelExists = !isEmpty(resolveKakousijiGridPanelByType(mappedType));
        const targetType = sourcePanelExists
            ? sourceType
            : (mappedPanelExists ? mappedType : (isKakousijiPanelType(sourceType) ? sourceType : mappedType));
        return targetType;
    }
    function normalizeKakousijiDetailForHinbanPanels(sourceDetail) {
        const mappedDetail = {};
        Object.keys(isEmpty(sourceDetail) ? {} : sourceDetail).forEach((key) => {
            const rawType = String(key ?? "").trim();
            const targetType = resolveKakousijiDetailTargetPanelType(rawType);
            if (targetType === "") return;
            const panelInfo = resolveKakousijiGridPanelByType(targetType);
            const sourceRows = DeepCopy(DicVal(sourceDetail, key, []));
            const displayRows = normalizeKakousijiSampleDetailForDisplayByType(targetType, sourceRows);
            const normalizedRows = isEmpty(panelInfo)
                ? displayRows
                : normalizeKakousijiDemandRows(panelInfo, displayRows);
            // デメリット(130)は表示列がUI専用のため、DBフラグ/コードから表示値を補完する(加工指示と同じ)。
            mappedDetail[targetType] = typeof normalizeKakousijiDemeritDisplayRows === "function"
                ? normalizeKakousijiDemeritDisplayRows(targetType, normalizedRows)
                : normalizedRows;
        });
        return mappedDetail;
    }
    // 受注数/納品予定数グリッドは表示用の集計・ラベル行(row_kind=subtotal/total/summary_label/summary_total)を
    // 含むため、保存明細からは除外する(加工指示の toDemandSaveRows と同じ挙動)。
    // row_kind は demand 表示行にしか付かないため、全セクションへ一律適用しても実データ行は落ちない。
    function stripKakousijiDemandSummaryRowsForSave(rows) {
        if (!Array.isArray(rows)) return rows;
        return rows
            .filter((row) => {
                const kind = String(DicVal(row, "row_kind", "") ?? "").trim();
                return kind !== "subtotal" && kind !== "total" && kind !== "summary_label" && kind !== "summary_total";
            })
            .map((row) => {
                if (isEmpty(row)) return row;
                const next = { ...row };
                delete next.row_kind;
                delete next.grip;
                delete next.expand;
                return next;
            });
    }
    function normalizeKakousijiDetailForSave(sourceDetail) {
        const mappedDetail = {};
        Object.keys(isEmpty(sourceDetail) ? {} : sourceDetail).forEach((key) => {
            const rawType = String(key ?? "").trim();
            if (rawType === "") return;
            mappedDetail[rawType] = DeepCopy(stripKakousijiDemandSummaryRowsForSave(
                normalizeKakousijiSampleDetailForStoreByType(rawType, DicVal(sourceDetail, key, []))
            ));
        });
        return mappedDetail;
    }
    function importKakousijiEntriesFromHinbanData(entries) {
        const sourceEntries = Array.isArray(entries) ? entries : [];
        const importedRows = [];
        sourceEntries.forEach((entry, index) => {
            const ssdNo = String(DicVal(entry, "SSD_NO", DicVal(DicVal(entry, "data", {}), "SSD_NO", ""))).trim();
            if (ssdNo === "") return;
            const normalizedEntryDetail = normalizeKakousijiDetailForHinbanPanels(DicVal(entry, "detail", {}));
            setKakousijiEntryCache(ssdNo, {
                data: DeepCopy(DicVal(entry, "data", {})),
                detail: normalizedEntryDetail,
                orderHistory: DeepCopy(DicVal(entry, "orderHistory", {}))
            });
            const summaryRow = buildKakousijiSummaryRowFromEntry(entry, index, resolveCurrentOppiCode());
            if (!isEmpty(summaryRow)) importedRows.push(summaryRow);
        });
        const groupedRows = groupKakousijiSampleSummaryRows(importedRows);
        kakousijiImportedSummaryRowsRef.current = groupedRows;
        setKakousijiSummaryList(groupedRows);
        setActiveKakousijiKey((prev) => groupedRows.some((row) => row.key === prev) ? prev : (groupedRows.length === 0 ? "" : groupedRows[0].key));
        syncKakousijiAnchorGridRows(groupedRows);
    }
    function snapshotActiveKakousijiSelectionData() {
        const activeItem = activeKakousijiSummaryItem();
        if (isEmpty(activeItem)) return;
        storeRowData();
        if (isKakousijiSampleGroupSummaryItem(activeItem)) {
            const activeSampleEntryKey = activeKakousijiSampleGroupEntryKey(activeItem);
            const entryIndex = kakousijiSampleGroupEntryKeys(activeItem).findIndex((key) => String(key).trim() === String(activeSampleEntryKey).trim());
            snapshotKakousijiSampleGroupDisplayEntry(activeItem, activeSampleEntryKey, entryIndex < 0 ? 0 : entryIndex);
            return;
        }
        const data = {};
        collectKakousijiPanelColumnIds().forEach((columnId) => {
            data[columnId] = DicVal(entrydataRef.current, columnId, "");
        });
        const detail = {};
        (Array.isArray(gridlist) ? gridlist : []).forEach((item) => {
            if (!isKakousijiPanelType(DicVal(item, "type", ""))) return;
            const type = String(DicVal(item, "type", "")).trim();
            if (type === HINBAN_WASH_PANEL_TYPE && !isEmpty(kakousijiWashSourceRowsRef) && Array.isArray(kakousijiWashSourceRowsRef.current) && kakousijiWashSourceRowsRef.current.length !== 0) {
                // 洗濯表示(131): グリッドには代表1行しか無いため、保持している全ソース行へ
                // 表示行(編集反映済み)を rowid で書き戻して保存用の明細にする。
                const sourceRows = DeepCopy(kakousijiWashSourceRowsRef.current);
                const saveRows = typeof buildHinbanWashSaveRows === "function" ? buildHinbanWashSaveRows(GridData(item) || []) : [];
                const saveRow = Array.isArray(saveRows) && saveRows.length !== 0 ? saveRows[0] : null;
                if (!isEmpty(saveRow)) {
                    const saveRowId = String(DicVal(saveRow, "rowid", "") ?? "").trim();
                    const index = sourceRows.findIndex((row) => String(DicVal(row, "rowid", "") ?? "").trim() === saveRowId);
                    if (index >= 0) sourceRows[index] = { ...sourceRows[index], ...saveRow };
                    else sourceRows.push(saveRow);
                }
                detail[type] = sourceRows;
                return;
            }
            detail[type] = DeepCopy(stripKakousijiDemandSummaryRowsForSave(
                normalizeKakousijiSampleDetailForStoreByType(type, GridData(item) || [])
            ));
        });
        const cacheKey = isKakousijiSampleGroupSummaryItem(activeItem)
            ? activeKakousijiSampleGroupEntryKey(activeItem)
            : String(DicVal(activeItem, 'key', '')).trim();
        // gridlist に無いパネル(副資材ベース120等はグリッド外)の明細は
        // 既存キャッシュから引き継ぐ。ここで欠落させると切替復帰時に復元できない。
        const previousDetail = DicVal(getKakousijiEntryCache(cacheKey), "detail", {});
        Object.keys(isEmpty(previousDetail) ? {} : previousDetail).forEach((type) => {
            if (!(type in detail)) detail[type] = DeepCopy(previousDetail[type]);
        });
        setKakousijiEntryCache(cacheKey, { data: data, detail: detail });
        if (isKakousijiSampleGroupSummaryItem(activeItem)) {
            setKakousijiEntryCache(KAKOUSIJI_SAMPLE_GROUP_KEY, { data: data, detail: detail });
        }
    }
    function switchActiveKakousijiKey(nextKey) {
        const normalizedNextKey = String(nextKey ?? "").trim();
        const currentKey = String(activeKakousijiKeyRef.current ?? "").trim();
        if (normalizedNextKey === currentKey) return;
        snapshotActiveKakousijiSelectionData();
        activeKakousijiKeyRef.current = normalizedNextKey;
        setActiveKakousijiKey(normalizedNextKey);
    }
    function normalizeKakousijiOrderHistory(source) {
        const src = isEmpty(source) ? {} : source;
        const pick = (key) => (Array.isArray(DicVal(src, key, null)) ? DicVal(src, key, []) : []);
        return { seihin: pick("seihin"), sozai: pick("sozai"), fuzoku: pick("fuzoku"), fukushizai: pick("fukushizai") };
    }
    function applyActiveKakousijiSelectionData(sourceData, sourceDetail, sourceOrderHistory = null) {
        // 発注履歴は指示切替時のみ差し替える(null はスナップショット復元等の「維持」)
        if (sourceOrderHistory !== null && typeof setKakousijiOrderHistoryData === "function") {
            setKakousijiOrderHistoryData(normalizeKakousijiOrderHistory(sourceOrderHistory));
        }
        const nextEntry = isEmpty(entrydataRef.current) ? {} : { ...entrydataRef.current };
        collectKakousijiPanelColumnIds().forEach((columnId) => {
            nextEntry[columnId] = DicVal(sourceData, columnId, "");
        });
        assignEntryDataCurrent(applyClientOnlyEntryDefaults(nextEntry), "applyActiveKakousijiSelectionData");
        const mappedDetail = normalizeKakousijiDetailForHinbanPanels(sourceDetail);
        const fukushizaiBaseRows = Array.isArray(DicVal(mappedDetail, KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE, null))
            ? DicVal(mappedDetail, KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE, [])
            : [];
        const fukushizaiBaseContext = resolveKakousijiGroupItemContext(DicVal(fukushizaiBaseRows, 0, null));
        const qualityBaseRows = Array.isArray(DicVal(mappedDetail, KAKOUSIJI_QUALITY_BASE_PANEL_TYPE, null))
            ? DicVal(mappedDetail, KAKOUSIJI_QUALITY_BASE_PANEL_TYPE, [])
            : [];
        const qualityBaseContext = resolveKakousijiGroupItemContext(DicVal(qualityBaseRows, 0, null));
        if (qualityBaseContext.itemCd !== "") {
            kakousijiQualityGroupControlStateRef.current = {
                ...kakousijiQualityGroupControlStateRef.current,
                itemCd: qualityBaseContext.itemCd,
                groupNo: qualityBaseContext.groupNo
            };
        }
        if (fukushizaiBaseContext.itemCd !== "") {
            kakousijiGroupControlStateRef.current = {
                ...kakousijiGroupControlStateRef.current,
                itemCd: fukushizaiBaseContext.itemCd,
                groupNo: fukushizaiBaseContext.groupNo,
                sourceItemCd: fukushizaiBaseContext.itemCd,
                sourceGroupNo: fukushizaiBaseContext.groupNo
            };
        }
        const appliedDetailPanelTypes = [];
        (Array.isArray(gridlist) ? gridlist : []).forEach((item) => {
            if (!isKakousijiPanelType(DicVal(item, "type", ""))) return;
            const targetType = String(DicVal(item, "type", "")).trim();
            appliedDetailPanelTypes.push(targetType);
            const targetRows = DeepCopy(DicVal(mappedDetail, targetType, []));
            if (targetType === HINBAN_WASH_PANEL_TYPE && typeof pickKakousijiQualityScopeRepresentativeRows === "function" && !isEmpty(kakousijiWashSourceRowsRef)) {
                // 洗濯表示(131): 全ソース行を保持し、現在の品質選択の代表行だけを表示行へ渡す
                // (加工指示の代表行表示と同じ。WashGrid は先頭1行を表示行に変換する)。
                kakousijiWashSourceRowsRef.current = targetRows;
                const representative = pickKakousijiQualityScopeRepresentativeRows(targetType, targetRows);
                setEntryGridRows({ id: item.id, type: targetType }, representative.length === 0 ? [] : [representative[0]]);
                return;
            }
            setEntryGridRows({ id: item.id, type: targetType }, targetRows);
        });
        if (!appliedDetailPanelTypes.includes(KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE) && fukushizaiBaseRows.length !== 0) {
            const panel120 = resolveKakousijiGridPanelByType(KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE);
            if (!isEmpty(panel120)) {
                setEntryGridRows({ id: panel120.id, type: KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE }, DeepCopy(fukushizaiBaseRows));
            }
        }
        // 品質表示ベース行(125)も同様に保持する。ベースパネルはグリッドではないため
        // 明細適用ループの対象外で、ここで保持しないと代表行投影(品質表示の単票項目)の
        // 取得元が常に空になり、項目がクリアされたままになる。
        if (!appliedDetailPanelTypes.includes(KAKOUSIJI_QUALITY_BASE_PANEL_TYPE) && qualityBaseRows.length !== 0) {
            const panel125 = resolveKakousijiGridPanelByType(KAKOUSIJI_QUALITY_BASE_PANEL_TYPE);
            if (!isEmpty(panel125)) {
                setEntryGridRows({ id: panel125.id, type: KAKOUSIJI_QUALITY_BASE_PANEL_TYPE }, DeepCopy(qualityBaseRows));
            }
        }
        syncKakousijiSamplePanelsFromPdtc();
        if (fukushizaiBaseContext.itemCd !== "") {
            setKakousijiGroupControlState((prev) => ({
                ...prev,
                itemCd: fukushizaiBaseContext.itemCd,
                groupNo: fukushizaiBaseContext.groupNo,
                sourceItemCd: fukushizaiBaseContext.itemCd,
                sourceGroupNo: fukushizaiBaseContext.groupNo
            }));
            const projectionChanged = mergeKakousijiRepresentativeRowToEntry(KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE, {
                itemCd: fukushizaiBaseContext.itemCd,
                groupNo: fukushizaiBaseContext.groupNo
            });
        }
        if (qualityBaseContext.itemCd !== "") {
            setKakousijiQualityGroupControlState((prev) => ({
                ...prev,
                itemCd: qualityBaseContext.itemCd,
                groupNo: qualityBaseContext.groupNo
            }));
        }
        // 副資材スコープ4グリッド(121-124)はベース行(120)の代表行から表示行を組み立てる。
        // detail には 121-124 が存在しない(加工指示と同じくクライアント側で縦持ち展開する)。
        if (typeof applyFukushizaiScopeDisplayRows === "function") {
            applyFukushizaiScopeDisplayRows(fukushizaiBaseRows);
        }
        // 納品予定(138)は行投入後に列定義(2段日付ヘッダ)を作り直す。
        if (typeof refreshKakousijiNouhinGridColumns === "function") {
            refreshKakousijiNouhinGridColumns();
        }
        dispatch(entrydata({ ...entrydataRef.current }));
        dispatch(entryrerender());
    }
    function resolveInitialProductionKakousijiItem() {
        const rows = (Array.isArray(kakousijiSummaryList) ? kakousijiSummaryList : [])
            .filter((item) => normalizeKakousijiKubunValue(DicVal(item, "kubunValue", "")) === "1" && String(DicVal(item, "code", "")).trim() !== "");
        if (rows.length === 0) return null;
        return [...rows].sort((a, b) =>
            String(DicVal(a, "code", "")).localeCompare(String(DicVal(b, "code", "")), "ja", { numeric: true })
        )[0];
    }

    return {
        mapKakousijiPanelTypeToHinban,
        mapHinbanPanelTypeToKakousiji,
        collectKakousijiPanelColumnIds,
        collectBasePanelColumnIds,
        clearActiveKakousijiSelectionData,
        buildKakousijiEntrySnapshotData,
        buildKakousijiEntrySnapshotDetail,
        emptyKakousijiSnapshot,
        setKakousijiEntryCache,
        getKakousijiEntryCache,
        getKakousijiEntryCacheForSummaryItem,
        resolveKakousijiDetailTargetPanelType,
        normalizeKakousijiDetailForHinbanPanels,
        normalizeKakousijiDetailForSave,
        importKakousijiEntriesFromHinbanData,
        snapshotActiveKakousijiSelectionData,
        switchActiveKakousijiKey,
        applyActiveKakousijiSelectionData,
        resolveInitialProductionKakousijiItem
    };
}
